import Ciudades from '../assets/data/ciudades.json'
import {
  calculatePackageDimensions,
  tipoServicioFromPeso
} from './packageDimensionsFromProducts'
import { resolveCityCodeFromLabel } from './shippingDestinationCityCode'

const medellin = (Ciudades as Array<{ codigo: string; ciudad: string }>).find(city => city.codigo === '05001')

const QUOTE_ORIGIN = {
  origenNombre: 'Mundial de mercancias',
  origenEmpresa: 'Mundial de mercancias',
  origenEmail: 'info@mdm.com.co',
  origenCelular: '3014017136',
  origenPais: 'Colombia',
  origenDepartamento: 'Antioquia',
  origenCiudad: medellin?.ciudad || 'Medellín',
  origenDireccion: 'Calle 25 A # 43B - 199, Medellin, Antioquia',
  origenNit: '9009978173'
}

const CARRIER_TO_TRANSPORTADORA: Record<string, string> = {
  'Envía': 'envía',
  Noventa9Minutos: 'noventa9minutos',
  InterRapidisimo: 'inter_rapidisimo',
  Deprisa: 'deprisa',
  Coordinadora: 'coordinadora'
}

interface QuoteLine {
  product_id: string
  sku?: string
  product_name?: string
  quantity: number
}

interface QuoteAddress {
  address_1?: string
  address_2?: string
  city?: string
  state?: string
  phone?: string
}

export interface OrderQuoteSource {
  lineItems?: QuoteLine[]
  shippingAddress?: QuoteAddress
  customer?: {
    first_name?: string
    last_name?: string
    email?: string
    document?: string
  }
  extraDetails?: {
    payment_method?: string
  }
  payment_method?: string
}

export function mapQuoteCarrierToTransportadora(carrier: string): string {
  return CARRIER_TO_TRANSPORTADORA[carrier] || ''
}

function detailForLine(item: QuoteLine, productDetails: Record<string, any>) {
  const sku = item.sku ? String(item.sku).replace(/^0+/, '') : ''
  return productDetails[item.product_id]
    || (item.sku ? productDetails[item.sku] : undefined)
    || (sku ? productDetails[sku] : undefined)
}

export function packageDimensionsForOrderLines(
  lineItems: QuoteLine[],
  productDetails: Record<string, any>
) {
  const keyed: Record<string, any> = { ...productDetails }
  lineItems.forEach(item => {
    const detail = detailForLine(item, productDetails)
    if (detail && item.product_id) keyed[item.product_id] = detail
  })
  return calculatePackageDimensions(lineItems, keyed)
}

export interface ManualPackageDimensions {
  largo: number
  ancho: number
  altura: number
  peso: number
}

function packageIsComplete(dims: { largo: number; ancho: number; altura: number; peso: number }): boolean {
  return dims.largo > 0 && dims.ancho > 0 && dims.altura > 0 && dims.peso > 0
}

export function hasCalculatedPackageDimensions(
  order: OrderQuoteSource,
  productDetails: Record<string, any>
): boolean {
  return packageIsComplete(packageDimensionsForOrderLines(order.lineItems || [], productDetails))
}

export function isValidManualPackage(dims: ManualPackageDimensions): boolean {
  return dims.largo >= 1 && dims.ancho >= 1 && dims.altura >= 1 && dims.peso >= 0.1
}

export function canQuoteOrderShipment(
  order: OrderQuoteSource,
  _productDetails: Record<string, any>
): boolean {
  const lineItems = order.lineItems || []
  if (!lineItems.length) return false

  const address = order.shippingAddress
  const city = String(address?.city || '').trim()
  const state = String(address?.state || '').trim()
  const street = String(address?.address_1 || '').trim()
  if (!state || !city || !street) return false
  if (!resolveCityCodeFromLabel(city)) return false

  return true
}

export function buildOrderQuoteRequest(
  order: OrderQuoteSource,
  productDetails: Record<string, any>,
  manualDimensions?: ManualPackageDimensions | null
) {
  if (!canQuoteOrderShipment(order, productDetails)) return null

  const lineItems = order.lineItems || []
  const address = order.shippingAddress || {}
  const calculated = packageDimensionsForOrderLines(lineItems, productDetails)
  const dims = packageIsComplete(calculated)
    ? calculated
    : (manualDimensions && isValidManualPackage(manualDimensions) ? manualDimensions : null)
  if (!dims) return null
  const paymentMethod = order.extraDetails?.payment_method || order.payment_method || ''
  const address2 = String(address.address_2 || '').trim()
  const street = `${String(address.address_1 || '').trim()}${address2 ? `, ${address2}` : ''}`

  const formData = {
    ...QUOTE_ORIGIN,
    destinoNombre: '',
    destinoEmail: '',
    destinoCelular: address.phone || '',
    destinoPais: 'Colombia',
    destinoDepartamento: address.state || '',
    destinoCiudad: address.city || '',
    destinoDireccion: street,
    destinoDireccion_2: address2,
    destinoNit: '',
    contenido: lineItems.map(item => item.product_name).filter(Boolean).join(', '),
    largo: dims.largo,
    ancho: dims.ancho,
    altura: dims.altura,
    peso: dims.peso,
    valorDeclarado: 0,
    metodoPago: paymentMethod === 'cod' ? 'cod' : paymentMethod
  }

  return {
    formData,
    orderDetail: {
      customer: order.customer,
      shippingAddress: address,
      lineItems
    },
    tipoServicio: tipoServicioFromPeso(Number(dims.peso) || 0)
  }
}
